import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { apiClient } from "../../../api/httpClient";
import {
  formatRelativeTime,
  getBondCacheResumePath,
  getBondUpdateUrl,
} from "../../../globals/utils/homeHelpers";
import {
  getActivityFeedDescription,
  PG_ACTIVITY_MAX_DESC_LEN,
} from "../../SHARED/pgHomeActivityText";
import { renderHighlightedActivityText } from "../../SHARED/pgHomeActivityHighlight";
import {
  buildLawFirmDashboardLinkGroups,
  getLawFirmHomeVariantConfig,
  LF_HOME_VARIANT,
} from "../lawFirmHomeConfig";
import { useLawFirmIdentityBootstrap } from "./useLawFirmIdentityBootstrap";
import { useLawFirmHomeData } from "./useLawFirmHomeData";

/**
 * Law-firm user / admin home dashboard data (no JSX).
 * @param {'user'|'admin'} variant
 */
function useLawFirmUserHome(variant = LF_HOME_VARIANT.user) {
  const cfg = getLawFirmHomeVariantConfig(variant);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const { user } = auth;
  const lawFirmState = useSelector((state) => state.lawfirm || {});

  const identity = useLawFirmIdentityBootstrap(user);
  const homeData = useLawFirmHomeData(
    identity.lawFirmIdState,
    user?.id,
    lawFirmState.lawfirmId
  );

  const [unassignedBonds, setUnassignedBonds] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);

  const loadUnassignedBonds = useCallback(async () => {
    if (!identity.lawFirmIdState) return;
    try {
      setLoadingUnassigned(true);
      const data = await apiClient(
        `/unassigned-paralegal-bonds?lawfirm_id=${identity.lawFirmIdState}`,
        "GET"
      );
      setUnassignedBonds(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load unassigned bonds", e);
      setUnassignedBonds([]);
    } finally {
      setLoadingUnassigned(false);
    }
  }, [identity.lawFirmIdState]);

  useEffect(() => {
    loadUnassignedBonds();
  }, [identity.lawFirmIdState, location.pathname, loadUnassignedBonds]);

  const getBondUrl = useCallback(
    (bondType, bondId) => getBondUpdateUrl(bondType, bondId),
    []
  );

  const getCacheRoute = useCallback(
    (bondType, cacheId) => getBondCacheResumePath(bondType, cacheId),
    []
  );

  const getActivityText = useCallback(
    (item) => getActivityFeedDescription(item),
    []
  );

  const renderHighlighted = useCallback(
    (text, item) => renderHighlightedActivityText(text, item),
    []
  );

  const linkGroups = useMemo(
    () => buildLawFirmDashboardLinkGroups(variant, identity.lawFirmIdState),
    [variant, identity.lawFirmIdState]
  );

  const home = {
    drafts: homeData.drafts,
    activity: homeData.activity,
    indemnity: homeData.indemnity,
    dialogs: homeData.dialogs,
    compliance: homeData.compliance,
  };

  const { dialogs, compliance, indemnity, activity, drafts } = homeData;

  return {
    cfg,
    variant,
    linkGroups,
    loading: identity.loading,
    navigate,
    home,
    ...identity,
    activities: activity.activities,
    loadingActivities: activity.loadingActivities,
    selectedActivity: activity.selectedActivity,
    setSelectedActivity: activity.setSelectedActivity,
    hasMoreActivities: activity.hasMoreActivities,
    loadMoreActivities: activity.loadMoreActivities,
    MAX_DESC_LEN: PG_ACTIVITY_MAX_DESC_LEN,
    getActivityText,
    renderHighlighted,
    formatRelativeTime,
    unassignedBonds,
    loadingUnassigned,
    getBondUrl,
    missingIndemnityBonds: indemnity.bonds,
    loadingIndemnity: indemnity.loading,
    markingIndemnity: indemnity.markingIndemnity,
    handleMarkIndemnityClick: indemnity.handleMarkIndemnityClick,
    cachedBonds: drafts.cachedBonds,
    loadingDrafts: drafts.loadingDrafts,
    getCacheRoute,
    handleDeleteDraft: drafts.handleDeleteDraftClick,
    deletingId: drafts.deletingId,
    indemnityDialogOpen: dialogs.indemnityDialogOpen,
    setIndemnityDialogOpen: (open) => {
      if (!open) dialogs.closeIndemnityDialog();
    },
    setSelectedBondForIndemnity: (bond) => {
      if (bond) indemnity.handleMarkIndemnityClick(bond);
    },
    selectedBondForIndemnity: dialogs.selectedBondForIndemnity,
    indemnityOption: dialogs.indemnityOption,
    setIndemnityOption: dialogs.setIndemnityOption,
    selectedFiles: dialogs.selectedFiles,
    setSelectedFiles: () => {},
    handleFilesSelected: dialogs.handleFilesSelected,
    handleClearFiles: dialogs.handleClearFiles,
    handleRemoveFile: dialogs.handleRemoveFile,
    handleViewFile: dialogs.handleViewFile,
    handleMarkIndemnityOffline: dialogs.handleMarkIndemnityOffline,
    handleSubmitIndemnityFiles: dialogs.handleSubmitIndemnityFiles,
    isSubmitting: dialogs.isSubmitting,
    processingOpen: dialogs.processingOpen,
    validationOpen: dialogs.validationOpen,
    setValidationOpen: dialogs.setValidationOpen,
    validationResults: dialogs.validationResults,
    hasBlockingErrors: compliance.hasBlockingErrors,
    hasPiiWarningsOnly: compliance.hasPiiWarningsOnly,
    handleFinalizeIndemnitySubmit: dialogs.handleFinalizeIndemnitySubmit,
    isFinalizing: dialogs.isFinalizing,
  };
}

export default useLawFirmUserHome;
